import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CANONICAL_STATES } from "@sales-ops-amplify/shared";
import {
  connectApollo,
  connectHubSpot,
  connectNewsApi,
  disconnectApollo,
  disconnectHubSpot,
  disconnectNewsApi,
  getIntegrationsStatus,
  retryFailedHubSpotOutbox,
  saveHubSpotStageMap,
  syncHubSpot,
} from "@/api/hubspot";
import {
  disconnectSalesforce,
  saveSalesforceStageMap,
  startSalesforceOAuth,
  syncSalesforce,
} from "@/api/salesforce";
import { connectSlack, disconnectSlack } from "@/api/slack";
import {
  disconnectGoogleCalendar,
  startGoogleOAuth,
} from "@/api/google-calendar";
import { getOrgWorkspace, updateOrg } from "@/api/org";
import { cn } from "@/cn";

const STATUS_KEY = ["integrations-status"] as const;
const ORG_KEY = ["org"] as const;

type StageMapRow = { providerLabel: string; zeroStage: string };

function stageMapToRows(map?: Record<string, string>): StageMapRow[] {
  const entries = Object.entries(map ?? {});
  if (entries.length === 0) {
    return [{ providerLabel: "", zeroStage: "Lead" }];
  }
  return entries.map(([providerLabel, zeroStage]) => ({
    providerLabel,
    zeroStage,
  }));
}

function rowsToStageMap(rows: StageMapRow[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const row of rows) {
    const key = row.providerLabel.trim();
    const val = row.zeroStage.trim();
    if (key && val) out[key] = val;
  }
  return out;
}

export function SettingsIntegrationsPage() {
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: status, isLoading } = useQuery({
    queryKey: STATUS_KEY,
    queryFn: getIntegrationsStatus,
    staleTime: 15_000,
  });

  const { data: workspace } = useQuery({
    queryKey: ORG_KEY,
    queryFn: getOrgWorkspace,
    staleTime: 30_000,
  });
  const org = workspace?.org ?? null;

  const [apolloKey, setApolloKey] = useState("");
  const [newsApiKey, setNewsApiKey] = useState("");
  const [hubspotToken, setHubspotToken] = useState("");
  const [slackWebhook, setSlackWebhook] = useState("");
  const [showApolloKey, setShowApolloKey] = useState(false);
  const [showNewsApiKey, setShowNewsApiKey] = useState(false);
  const [showHubspotToken, setShowHubspotToken] = useState(false);
  const [updatingHubspot, setUpdatingHubspot] = useState(false);
  const [stageRows, setStageRows] = useState<StageMapRow[]>([
    { providerLabel: "", zeroStage: "Lead" },
  ]);
  const [sfStageRows, setSfStageRows] = useState<StageMapRow[]>([
    { providerLabel: "", zeroStage: "Lead" },
  ]);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (status?.hubspot?.stageMap) {
      setStageRows(stageMapToRows(status.hubspot.stageMap));
    }
  }, [status?.hubspot?.stageMap]);

  useEffect(() => {
    if (status?.salesforce?.stageMap) {
      setSfStageRows(stageMapToRows(status.salesforce.stageMap));
    }
  }, [status?.salesforce?.stageMap]);

  useEffect(() => {
    const sf = searchParams.get("sf");
    const google = searchParams.get("google");
    if (!sf && !google) return;
    if (sf === "connected") {
      setMessage("Salesforce connected.");
      setError(null);
      qc.invalidateQueries({ queryKey: STATUS_KEY });
    } else if (sf === "error") {
      setError(
        searchParams.get("message") ?? "Salesforce connection failed",
      );
    }
    if (google === "connected") {
      setMessage("Google Workspace connected.");
      setError(null);
      qc.invalidateQueries({ queryKey: STATUS_KEY });
    } else if (google === "error") {
      setError(
        searchParams.get("message") ?? "Google connection failed",
      );
    }
    const next = new URLSearchParams(searchParams);
    next.delete("sf");
    next.delete("google");
    next.delete("message");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, qc]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: STATUS_KEY });
    qc.invalidateQueries({ queryKey: ["hubspot-status"] });
    qc.invalidateQueries({ queryKey: ["profile"] });
  };

  const apolloConnect = useMutation({
    mutationFn: () => connectApollo(apolloKey.trim()),
    onSuccess: (res) => {
      setMessage(res.message ?? "Apollo API key saved.");
      setError(null);
      setApolloKey("");
      invalidate();
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : "Failed to save Apollo key");
    },
  });

  const apolloDisconnect = useMutation({
    mutationFn: disconnectApollo,
    onSuccess: () => {
      setMessage("Apollo disconnected.");
      setError(null);
      invalidate();
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : "Failed to disconnect Apollo");
    },
  });

  const newsapiConnect = useMutation({
    mutationFn: () => connectNewsApi(newsApiKey.trim()),
    onSuccess: (res) => {
      setMessage(res.message ?? "NewsAPI key saved.");
      setError(null);
      setNewsApiKey("");
      invalidate();
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : "Failed to save NewsAPI key");
    },
  });

  const newsapiDisconnect = useMutation({
    mutationFn: disconnectNewsApi,
    onSuccess: () => {
      setMessage("NewsAPI disconnected.");
      setError(null);
      invalidate();
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : "Failed to disconnect NewsAPI");
    },
  });

  const hubspotConnect = useMutation({
    mutationFn: () =>
      connectHubSpot(hubspotToken.trim(), rowsToStageMap(stageRows)),
    onSuccess: (res) => {
      setMessage(res.message ?? "HubSpot connected.");
      setError(null);
      setHubspotToken("");
      setUpdatingHubspot(false);
      invalidate();
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : "Failed to connect HubSpot");
    },
  });

  const hubspotDisconnect = useMutation({
    mutationFn: disconnectHubSpot,
    onSuccess: () => {
      setMessage("HubSpot disconnected.");
      setError(null);
      setUpdatingHubspot(false);
      invalidate();
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : "Failed to disconnect");
    },
  });

  const hubspotSync = useMutation({
    mutationFn: syncHubSpot,
    onSuccess: (res) => {
      const warn =
        res.warnings?.length ? ` Warnings: ${res.warnings.join(" ")}` : "";
      setMessage(
        `Synced — companies +${res.companies.created}/~${res.companies.updated}` +
          ` (fetched ${res.companies.fetched ?? "?"}), ` +
          `contacts +${res.contacts.created}/~${res.contacts.updated}` +
          ` (fetched ${res.contacts.fetched ?? "?"}), ` +
          `deals +${res.deals.created}/~${res.deals.updated}` +
          ` (fetched ${res.deals.fetched ?? "?"}).` +
          warn,
      );
      setError(null);
      invalidate();
      qc.invalidateQueries({ queryKey: ["opportunities"] });
      qc.invalidateQueries({ queryKey: ["leads"] });
      qc.invalidateQueries({ queryKey: ["companies"] });
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : "Sync failed");
      invalidate();
    },
  });

  const stageMapSave = useMutation({
    mutationFn: () => saveHubSpotStageMap(rowsToStageMap(stageRows)),
    onSuccess: (res) => {
      setMessage(res.message ?? "Stage map saved.");
      setError(null);
      invalidate();
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : "Failed to save stage map");
    },
  });

  const hubspotOutboxRetry = useMutation({
    mutationFn: retryFailedHubSpotOutbox,
    onSuccess: (res) => {
      setMessage(res.message ?? `Requeued ${res.reset} failed push job(s).`);
      setError(null);
      invalidate();
    },
    onError: (err: unknown) => {
      setError(
        err instanceof Error ? err.message : "Failed to retry push jobs",
      );
    },
  });

  const primaryCrmSave = useMutation({
    mutationFn: (primaryCrm: "hubspot" | "salesforce") =>
      updateOrg({ primaryCrm }),
    onSuccess: () => {
      setMessage("Primary CRM updated.");
      setError(null);
      qc.invalidateQueries({ queryKey: ORG_KEY });
    },
    onError: (err: unknown) => {
      setError(
        err instanceof Error ? err.message : "Failed to update primary CRM",
      );
    },
  });

  const salesforceConnect = useMutation({
    mutationFn: startSalesforceOAuth,
    onSuccess: (res) => {
      window.location.href = res.url;
    },
    onError: (err: unknown) => {
      setError(
        err instanceof Error ? err.message : "Failed to start Salesforce OAuth",
      );
    },
  });

  const salesforceDisconnect = useMutation({
    mutationFn: disconnectSalesforce,
    onSuccess: () => {
      setMessage("Salesforce disconnected.");
      setError(null);
      invalidate();
    },
    onError: (err: unknown) => {
      setError(
        err instanceof Error ? err.message : "Failed to disconnect Salesforce",
      );
    },
  });

  const salesforceSync = useMutation({
    mutationFn: syncSalesforce,
    onSuccess: (res) => {
      const warn =
        res.warnings?.length ? ` Warnings: ${res.warnings.join(" ")}` : "";
      setMessage(
        `Salesforce synced — accounts +${res.companies.created}/~${res.companies.updated}` +
          ` (fetched ${res.companies.fetched ?? "?"}), ` +
          `contacts +${res.contacts.created}/~${res.contacts.updated}` +
          ` (fetched ${res.contacts.fetched ?? "?"}), ` +
          `opportunities +${res.deals.created}/~${res.deals.updated}` +
          ` (fetched ${res.deals.fetched ?? "?"}).` +
          warn,
      );
      setError(null);
      invalidate();
      qc.invalidateQueries({ queryKey: ["opportunities"] });
      qc.invalidateQueries({ queryKey: ["leads"] });
      qc.invalidateQueries({ queryKey: ["companies"] });
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : "Salesforce sync failed");
      invalidate();
    },
  });

  const sfStageMapSave = useMutation({
    mutationFn: () => saveSalesforceStageMap(rowsToStageMap(sfStageRows)),
    onSuccess: (res) => {
      setMessage(res.message ?? "Salesforce stage map saved.");
      setError(null);
      invalidate();
    },
    onError: (err: unknown) => {
      setError(
        err instanceof Error ? err.message : "Failed to save Salesforce stage map",
      );
    },
  });

  const slackConnect = useMutation({
    mutationFn: () => connectSlack(slackWebhook.trim()),
    onSuccess: (res) => {
      setMessage(res.message ?? "Slack connected.");
      setError(null);
      setSlackWebhook("");
      invalidate();
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : "Failed to connect Slack");
    },
  });

  const slackDisconnect = useMutation({
    mutationFn: disconnectSlack,
    onSuccess: () => {
      setMessage("Slack disconnected.");
      setError(null);
      invalidate();
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : "Failed to disconnect Slack");
    },
  });

  const googleConnect = useMutation({
    mutationFn: startGoogleOAuth,
    onSuccess: (res) => {
      if (res.url) window.location.href = res.url;
    },
    onError: (err: unknown) => {
      setError(
        err instanceof Error ? err.message : "Failed to start Google OAuth",
      );
    },
  });

  const googleDisconnect = useMutation({
    mutationFn: disconnectGoogleCalendar,
    onSuccess: () => {
      setMessage("Google Workspace disconnected.");
      setError(null);
      invalidate();
    },
    onError: (err: unknown) => {
      setError(
        err instanceof Error ? err.message : "Failed to disconnect Google",
      );
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-brand" />
      </div>
    );
  }

  const apolloConnected = Boolean(status?.apollo.connected);
  const newsapiConnected = Boolean(status?.newsapi?.connected);
  const hubspotConnected = Boolean(status?.hubspot.connected);
  const salesforceConnected = Boolean(status?.salesforce?.connected);

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-foreground">Integrations</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Connected apps for CRM sync, calendar, Slack alerts, and enrichment.
          Credentials are saved to your account.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <span className="rounded-md border border-border bg-muted/20 px-3 py-1.5 text-xs font-medium">
            All Categories
          </span>
          <span className="rounded-md border border-border px-3 py-1.5 text-xs text-muted-foreground">
            Connected integrations
          </span>
          <span className="rounded-md border border-border px-3 py-1.5 text-xs text-muted-foreground">
            CRM
          </span>
          <span className="rounded-md border border-border px-3 py-1.5 text-xs text-muted-foreground">
            Calendar
          </span>
        </div>
      </div>

      {org ? (
        <section className="rounded-lg border border-border bg-card p-5 space-y-4">
          <div>
            <h3 className="font-medium text-foreground">Primary CRM</h3>
            <p className="text-xs text-muted-foreground mt-1">
              Workspace default provider for outbound sync and outbox pushes.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {(["hubspot", "salesforce"] as const).map((provider) => {
              const selected = (org.primaryCrm ?? "hubspot") === provider;
              return (
                <button
                  key={provider}
                  type="button"
                  onClick={() => primaryCrmSave.mutate(provider)}
                  disabled={primaryCrmSave.isPending || selected}
                  className={cn(
                    "rounded-md px-4 py-2 text-sm font-medium capitalize disabled:opacity-50",
                    selected
                      ? "bg-brand text-brand-foreground"
                      : "border border-border",
                  )}
                >
                  {provider}
                </button>
              );
            })}
          </div>
        </section>
      ) : null}

      {/* Apollo */}
      <section className="rounded-lg border border-border bg-card p-5 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-medium text-foreground">Apollo</h3>
            <p className="text-xs text-muted-foreground mt-1">
              API key for Lead import and market-pulse funding / tech / intent proxy.
            </p>
          </div>
          <StatusBadge connected={apolloConnected} />
        </div>

        {apolloConnected ? (
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => apolloDisconnect.mutate()}
              disabled={apolloDisconnect.isPending}
              className="rounded-md border border-border px-4 py-2 text-sm font-medium disabled:opacity-50"
            >
              {apolloDisconnect.isPending ? "Removing…" : "Remove key"}
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-foreground">API key</label>
              <div className="mt-1 flex gap-2">
                <input
                  type={showApolloKey ? "text" : "password"}
                  value={apolloKey}
                  onChange={(e) => setApolloKey(e.target.value)}
                  placeholder="Paste your Apollo API key"
                  autoComplete="off"
                  className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm"
                />
                <button
                  type="button"
                  onClick={() => setShowApolloKey((v) => !v)}
                  className="shrink-0 rounded-md border border-border px-3 text-xs text-muted-foreground"
                >
                  {showApolloKey ? "Hide" : "Show"}
                </button>
              </div>
            </div>
            <button
              type="button"
              onClick={() => apolloConnect.mutate()}
              disabled={!apolloKey.trim() || apolloConnect.isPending}
              className="rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-foreground disabled:opacity-50"
            >
              {apolloConnect.isPending ? "Saving…" : "Save Apollo key"}
            </button>
          </div>
        )}
      </section>

      {/* NewsAPI */}
      <section className="rounded-lg border border-border bg-card p-5 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-medium text-foreground">NewsAPI</h3>
            <p className="text-xs text-muted-foreground mt-1">
              Key for market-pulse news mentions on Signals. Falls back to server{" "}
              <code className="rounded bg-muted px-1">NEWSAPI_KEY</code> when unset.
            </p>
          </div>
          <StatusBadge connected={newsapiConnected} />
        </div>

        {newsapiConnected ? (
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => newsapiDisconnect.mutate()}
              disabled={newsapiDisconnect.isPending}
              className="rounded-md border border-border px-4 py-2 text-sm font-medium disabled:opacity-50"
            >
              {newsapiDisconnect.isPending ? "Removing…" : "Remove key"}
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-foreground">API key</label>
              <div className="mt-1 flex gap-2">
                <input
                  type={showNewsApiKey ? "text" : "password"}
                  value={newsApiKey}
                  onChange={(e) => setNewsApiKey(e.target.value)}
                  placeholder="Paste your NewsAPI.org key"
                  autoComplete="off"
                  className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm"
                />
                <button
                  type="button"
                  onClick={() => setShowNewsApiKey((v) => !v)}
                  className="shrink-0 rounded-md border border-border px-3 text-xs text-muted-foreground"
                >
                  {showNewsApiKey ? "Hide" : "Show"}
                </button>
              </div>
            </div>
            <button
              type="button"
              onClick={() => newsapiConnect.mutate()}
              disabled={!newsApiKey.trim() || newsapiConnect.isPending}
              className="rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-foreground disabled:opacity-50"
            >
              {newsapiConnect.isPending ? "Saving…" : "Save NewsAPI key"}
            </button>
          </div>
        )}
      </section>

      {/* HubSpot */}
      <section className="rounded-lg border border-border bg-card p-5 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-medium text-foreground">HubSpot</h3>
            <p className="text-xs text-muted-foreground mt-1">
              Private app access token. Syncs contacts, companies, and deals with
              your pipeline.
            </p>
          </div>
          <StatusBadge connected={hubspotConnected} />
        </div>

        {hubspotConnected && (
          <dl className="grid gap-2 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-xs text-muted-foreground">Portal ID</dt>
              <dd className="text-foreground">{status?.hubspot.portalId ?? "—"}</dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">Last sync</dt>
              <dd className="text-foreground">
                {status?.hubspot.lastSyncAt
                  ? new Date(status.hubspot.lastSyncAt).toLocaleString()
                  : "Never"}
              </dd>
            </div>
            {(status?.hubspot.pendingOutboxCount ?? 0) > 0 ? (
              <div>
                <dt className="text-xs text-muted-foreground">Pending push jobs</dt>
                <dd className="text-foreground">
                  {status?.hubspot.pendingOutboxCount}
                </dd>
              </div>
            ) : null}
            {(status?.hubspot.failedOutboxCount ?? 0) > 0 ? (
              <div>
                <dt className="text-xs text-muted-foreground">Failed push jobs</dt>
                <dd className="text-destructive">
                  {status?.hubspot.failedOutboxCount}
                </dd>
              </div>
            ) : null}
            {status?.hubspot.lastError ? (
              <div className="sm:col-span-2">
                <dt className="text-xs text-muted-foreground">Last error</dt>
                <dd className="text-destructive">{status.hubspot.lastError}</dd>
              </div>
            ) : null}
          </dl>
        )}

        {(!hubspotConnected || updatingHubspot) && (
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-foreground">
                Access token
              </label>
              <div className="mt-1 flex gap-2">
                <input
                  type={showHubspotToken ? "text" : "password"}
                  value={hubspotToken}
                  onChange={(e) => setHubspotToken(e.target.value)}
                  placeholder="pat-na1-…"
                  autoComplete="off"
                  className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm"
                />
                <button
                  type="button"
                  onClick={() => setShowHubspotToken((v) => !v)}
                  className="shrink-0 rounded-md border border-border px-3 text-xs text-muted-foreground"
                >
                  {showHubspotToken ? "Hide" : "Show"}
                </button>
              </div>
            </div>
            <StageMapEditor
              rows={stageRows}
              onChange={setStageRows}
              providerName="HubSpot"
              placeholder="HubSpot stage (e.g. appointmentscheduled)"
            />
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => hubspotConnect.mutate()}
                disabled={!hubspotToken.trim() || hubspotConnect.isPending}
                className="rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-foreground disabled:opacity-50"
              >
                {hubspotConnect.isPending
                  ? "Connecting…"
                  : hubspotConnected
                    ? "Update token"
                    : "Connect HubSpot"}
              </button>
              {updatingHubspot && (
                <button
                  type="button"
                  onClick={() => {
                    setUpdatingHubspot(false);
                    setHubspotToken("");
                  }}
                  className="rounded-md border border-border px-4 py-2 text-sm font-medium"
                >
                  Cancel
                </button>
              )}
            </div>
          </div>
        )}

        {hubspotConnected && !updatingHubspot && (
          <>
            <StageMapEditor
              rows={stageRows}
              onChange={setStageRows}
              providerName="HubSpot"
              placeholder="HubSpot stage (e.g. appointmentscheduled)"
            />
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => stageMapSave.mutate()}
                disabled={stageMapSave.isPending}
                className="rounded-md border border-border px-4 py-2 text-sm font-medium disabled:opacity-50"
              >
                {stageMapSave.isPending ? "Saving map…" : "Save stage map"}
              </button>
              <button
                type="button"
                onClick={() => hubspotSync.mutate()}
                disabled={hubspotSync.isPending}
                className="rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-foreground disabled:opacity-50"
              >
                {hubspotSync.isPending ? "Syncing…" : "Sync now"}
              </button>
              {(status?.hubspot.failedOutboxCount ?? 0) > 0 && (
                <button
                  type="button"
                  onClick={() => hubspotOutboxRetry.mutate()}
                  disabled={hubspotOutboxRetry.isPending}
                  className="rounded-md border border-border px-4 py-2 text-sm font-medium disabled:opacity-50"
                >
                  {hubspotOutboxRetry.isPending
                    ? "Retrying…"
                    : "Retry failed"}
                </button>
              )}
              <button
                type="button"
                onClick={() => setUpdatingHubspot(true)}
                className="rounded-md border border-border px-4 py-2 text-sm font-medium"
              >
                Update token
              </button>
              <button
                type="button"
                onClick={() => hubspotDisconnect.mutate()}
                disabled={hubspotDisconnect.isPending}
                className="rounded-md border border-border px-4 py-2 text-sm font-medium disabled:opacity-50"
              >
                {hubspotDisconnect.isPending ? "Disconnecting…" : "Disconnect"}
              </button>
            </div>
          </>
        )}
      </section>

      {/* Salesforce */}
      <section className="rounded-lg border border-border bg-card p-5 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-medium text-foreground">Salesforce</h3>
            <p className="text-xs text-muted-foreground mt-1">
              Connect via OAuth. Syncs Accounts, Contacts, and Opportunities with
              your pipeline.
            </p>
          </div>
          <StatusBadge connected={salesforceConnected} />
        </div>

        {salesforceConnected && (
          <dl className="grid gap-2 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-xs text-muted-foreground">Instance URL</dt>
              <dd className="truncate text-foreground">
                {status?.salesforce.instanceUrl ?? "—"}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">Last sync</dt>
              <dd className="text-foreground">
                {status?.salesforce.lastSyncAt
                  ? new Date(status.salesforce.lastSyncAt).toLocaleString()
                  : "Never"}
              </dd>
            </div>
            {(status?.salesforce.failedOutboxCount ?? 0) > 0 ? (
              <div>
                <dt className="text-xs text-muted-foreground">Failed push jobs</dt>
                <dd className="text-destructive">
                  {status?.salesforce.failedOutboxCount}
                </dd>
              </div>
            ) : null}
            {status?.salesforce.lastError ? (
              <div className="sm:col-span-2">
                <dt className="text-xs text-muted-foreground">Last error</dt>
                <dd className="text-destructive">{status.salesforce.lastError}</dd>
              </div>
            ) : null}
          </dl>
        )}

        {!salesforceConnected ? (
          <button
            type="button"
            onClick={() => salesforceConnect.mutate()}
            disabled={salesforceConnect.isPending}
            className="rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-foreground disabled:opacity-50"
          >
            {salesforceConnect.isPending ? "Opening…" : "Connect Salesforce"}
          </button>
        ) : (
          <>
            <StageMapEditor
              rows={sfStageRows}
              onChange={setSfStageRows}
              providerName="Salesforce"
              placeholder="Salesforce stage (e.g. Prospecting)"
            />
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => sfStageMapSave.mutate()}
                disabled={sfStageMapSave.isPending}
                className="rounded-md border border-border px-4 py-2 text-sm font-medium disabled:opacity-50"
              >
                {sfStageMapSave.isPending ? "Saving map…" : "Save stage map"}
              </button>
              <button
                type="button"
                onClick={() => salesforceSync.mutate()}
                disabled={salesforceSync.isPending}
                className="rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-foreground disabled:opacity-50"
              >
                {salesforceSync.isPending ? "Syncing…" : "Sync now"}
              </button>
              <button
                type="button"
                onClick={() => salesforceConnect.mutate()}
                disabled={salesforceConnect.isPending}
                className="rounded-md border border-border px-4 py-2 text-sm font-medium disabled:opacity-50"
              >
                Reconnect
              </button>
              <button
                type="button"
                onClick={() => salesforceDisconnect.mutate()}
                disabled={salesforceDisconnect.isPending}
                className="rounded-md border border-border px-4 py-2 text-sm font-medium disabled:opacity-50"
              >
                {salesforceDisconnect.isPending
                  ? "Disconnecting…"
                  : "Disconnect"}
              </button>
            </div>
          </>
        )}
      </section>

      {/* Slack */}
      <section className="rounded-lg border border-border bg-card p-5 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-medium text-foreground">Slack</h3>
            <p className="text-xs text-muted-foreground mt-1">
              Incoming webhook for stage and high-risk deal alerts.
            </p>
          </div>
          <StatusBadge connected={Boolean(status?.slack?.connected)} />
        </div>
        {status?.slack?.connected ? (
          <button
            type="button"
            onClick={() => slackDisconnect.mutate()}
            disabled={slackDisconnect.isPending}
            className="rounded-md border border-border px-4 py-2 text-sm font-medium disabled:opacity-50"
          >
            {slackDisconnect.isPending ? "Disconnecting…" : "Disconnect"}
          </button>
        ) : (
          <div className="space-y-3">
            <input
              type="url"
              value={slackWebhook}
              onChange={(e) => setSlackWebhook(e.target.value)}
              placeholder="https://hooks.slack.com/services/…"
              className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm"
            />
            <button
              type="button"
              onClick={() => slackConnect.mutate()}
              disabled={!slackWebhook.trim() || slackConnect.isPending}
              className="rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-foreground disabled:opacity-50"
            >
              {slackConnect.isPending ? "Saving…" : "Connect Slack"}
            </button>
          </div>
        )}
      </section>

      {/* Google Workspace (Calendar + Gmail) */}
      <section className="rounded-lg border border-border bg-card p-5 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-medium text-foreground">Google Workspace</h3>
            <p className="text-xs text-muted-foreground mt-1">
              Calendar plus Gmail read/send for Emails hub and Sequences.
              Reconnect if you previously linked without send. Manage mailbox
              status under Settings → Mailboxes.
            </p>
          </div>
          <StatusBadge connected={Boolean(status?.calendar?.connected)} />
        </div>
        {status?.email?.connected ? (
          <p className="text-xs text-emerald-600">
            Gmail linked — use Mailboxes to confirm send access
          </p>
        ) : status?.calendar?.connected ? (
          <p className="text-xs text-muted-foreground">
            Calendar connected — reconnect to add Gmail send scope.
          </p>
        ) : null}
        {status?.calendar?.connected ? (
          <button
            type="button"
            onClick={() => googleDisconnect.mutate()}
            disabled={googleDisconnect.isPending}
            className="rounded-md border border-border px-4 py-2 text-sm font-medium disabled:opacity-50"
          >
            {googleDisconnect.isPending ? "Disconnecting…" : "Disconnect"}
          </button>
        ) : (
          <button
            type="button"
            onClick={() => googleConnect.mutate()}
            disabled={googleConnect.isPending}
            className="rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-foreground disabled:opacity-50"
          >
            {googleConnect.isPending ? "Opening…" : "Connect Google"}
          </button>
        )}
      </section>

      {message && <p className="text-sm text-emerald-500">{message}</p>}
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
}

function StageMapEditor({
  rows,
  onChange,
  providerName,
  placeholder,
}: {
  rows: StageMapRow[];
  onChange: (rows: StageMapRow[]) => void;
  providerName: string;
  placeholder: string;
}) {
  return (
    <div className="space-y-2">
      <div>
        <p className="text-xs font-medium text-foreground">Stage map</p>
        <p className="text-xs text-muted-foreground mt-0.5">
          Map {providerName} deal stage labels to Zero pipeline stages.
        </p>
      </div>
      <div className="space-y-2">
        {rows.map((row, i) => (
          <div key={i} className="flex flex-wrap items-center gap-2">
            <input
              type="text"
              value={row.providerLabel}
              onChange={(e) => {
                const next = [...rows];
                next[i] = { ...row, providerLabel: e.target.value };
                onChange(next);
              }}
              placeholder={placeholder}
              className="min-w-[12rem] flex-1 rounded-md border border-border bg-bg px-3 py-1.5 text-sm"
            />
            <span className="text-xs text-muted-foreground">→</span>
            <select
              value={row.zeroStage}
              onChange={(e) => {
                const next = [...rows];
                next[i] = { ...row, zeroStage: e.target.value };
                onChange(next);
              }}
              className="rounded-md border border-border bg-bg px-3 py-1.5 text-sm"
            >
              {CANONICAL_STATES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => onChange(rows.filter((_, j) => j !== i))}
              disabled={rows.length <= 1}
              className="rounded-md border border-border px-2 py-1 text-xs text-muted-foreground disabled:opacity-40"
            >
              Remove
            </button>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={() =>
          onChange([...rows, { providerLabel: "", zeroStage: "Lead" }])
        }
        className="text-xs font-medium text-brand"
      >
        + Add mapping
      </button>
    </div>
  );
}

function StatusBadge({ connected }: { connected: boolean }) {
  return (
    <span
      className={cn(
        "shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium",
        connected
          ? "bg-emerald-500/15 text-emerald-600"
          : "bg-muted text-muted-foreground",
      )}
    >
      {connected ? "Connected" : "Not connected"}
    </span>
  );
}
